<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import { derived as sderived, writable } from 'svelte/store'
    import type { Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'

    import { iconCheck } from 'hydria/icons'
    import { gettext as _ } from 'hydria/services/gettext'
    import { simpleFetch } from 'hydria/services/session'
    import { asAny, spath } from 'hydria/svelte_helpers'
    import * as rt from 'hydria/utils/route'
    import { querystring } from 'hydria/utils/route'

    import { Breadcrumb } from 'breadcrumb'

    import type { User } from './User/user'

    import ExtraMetaEditor from 'admin/components/ExtraMetaEditor.svelte'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    type Status = 'loading' | 'ready' | 'all-done' | 'params' | 'error'
    const status: Writable<Status | null> = writable(null)

    // null -> status == ready, si status ready, user ne doit pas être null
    const user: Writable<User | null> = writable(null)

    const crumbs = sderived(status, ($status) => [
        { txt: $_('Users'), url: spath('/wdom-users') },
        {
            txt: $status == 'ready' && $user ? $user.email : $status == 'loading' ? '…' : iconCheck,
            html: $status != 'ready' && $status != 'loading',
            url: $status == 'ready' && $user ? spath(`/wdom-user/${$user.id}`) : undefined,
        },
        { txt: $_('Métadonnées supplémentaires (Batch)') },
    ])

    let loadedQs: string | undefined = undefined
    async function loadEntry(qs: string) {
        if (loadedQs == qs) {
            return
        }
        loadedQs = qs

        let sparams: { [key: string]: any }
        try {
            // @ts-expect-error
            sparams = JSON.parse(new URLSearchParams(qs).get('sparams'))
        } catch (e: any) {
            console.error('Invalid search parameters')
            status.set('params')
            return
        }
        if (!sparams) sparams = {}
        sparams.skip = 0
        sparams.limit = 1
        sparams.full_metadata = true

        status.set('loading')
        user.set(null)

        try {
            const r = await simpleFetch('GET', '/json/wdom-user/1/', { data: JSON.stringify(sparams, null, 0) })
            if (!r.data.items.length) {
                status.set('all-done')
            } else {
                // We don’t need ctime, so no point of DateTime-ing it.
                user.set(r.data.items[0])
                status.set('ready')
            }
        } catch (e) {
            console.error('Error loading user data:', e)
            status.set('error')
        }
    }

    onMount(() => {
        loadEntry($querystring)
        unsubs.push(
            querystring.subscribe(($qs) => {
                loadEntry($qs ? $qs : '')
            }),
        )
    })
</script>

{#if $status == 'all-done' || $status == 'params' || $status == 'error'}
    <Breadcrumb {crumbs} />

    {#if $status == 'all-done'}
        <div class="alert alert-success">
            <Icon inline icon="fa:check" />
            Il n’y a plus aucune entrée pour cette recherche.
        </div>
    {:else}
        <div class="alert alert-danger">
            <Icon inline icon="fa:exclamation-triangle" />
            {#if $status == 'params'}
                Les paramètres de recherche sont invalides.
            {:else}
                L’information sur un utilisateur n’a pas pu être chargée suite à une erreur.
            {/if}
        </div>
    {/if}
{:else}
    <ExtraMetaEditor {crumbs} user={asAny(user)} saved={() => loadEntry($querystring ? $querystring : '')} mode={'batch'} />
{/if}
